import type { Activity } from "../types";

/**
 * Turning a level's activities into a year that goes somewhere.
 *
 * ── The problem this replaces ───────────────────────────────────────────────
 * The day's six activities used to be drawn from the whole stage by hashing
 * (learner id + date). With 33 activities and six a day a child met the entire
 * library every six days, in a different order each time, forever. Nothing was
 * introduced, nothing was built on, nothing was ever finished — so a parent had
 * no answer to "where are we up to?" beyond a mastery meter that drifted.
 *
 * ── What this does instead ──────────────────────────────────────────────────
 * A term has an ordered list of activities. The day's six are drawn from a
 * WINDOW over that list which advances as the term does: a couple of genuinely
 * new activities each day, and the rest revisits of the ones introduced most
 * recently. So the child meets a new idea, then meets it again tomorrow, and
 * again a few days later — which is how a four-year-old comes to own counting,
 * and is the reason a level needs ~80 authored activities rather than 1,500.
 *
 * Deterministic in (day, level): two devices opening the same child's Tuesday
 * build the same Tuesday, and yesterday's plan is still yesterday's plan when
 * you scroll back to it.
 */

/** How many activities fill one day. Matches the six the app has always shown. */
export const ACTIVITIES_PER_DAY = 6;

/**
 * How many of the day's six are new.
 *
 * Two, not six. A day of all-new work is a day nothing is practised, and a day
 * of no new work is a day the child notices they are going in circles.
 */
const NEW_PER_DAY = 2;

/**
 * How far back a revisit may reach.
 *
 * Wide enough that an idea returns days later rather than only tomorrow —
 * spacing is what moves practice into memory — and narrow enough that Term 3
 * does not keep dragging the child back to the first week of Term 1.
 */
const REVISIT_WINDOW = 14;

export type Term = {
  id: string;
  level_id: string;
  term_index: number;
  title: string;
  goal_summary: string | null;
  goals: string[];
  start_day: number;
  end_day: number;
};

export type Level = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  stage: string;
  sort_order: number;
  days_total: number;
  is_active: boolean;
};

/** Small deterministic hash, so a day's ordering is stable without a PRNG. */
function hash(value: string): number {
  let out = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    out ^= value.charCodeAt(index);
    out = Math.imul(out, 16777619);
  }
  return (out >>> 0) / 4294967295;
}

/** The term a given day of the level falls in. */
export function termForDay(terms: Term[], dayIndex: number): Term | null {
  if (terms.length === 0) return null;
  const ordered = [...terms].sort((a, b) => a.term_index - b.term_index);
  return (
    ordered.find((term) => dayIndex >= term.start_day && dayIndex <= term.end_day) ??
    // Past the end of the level: hold at the final term rather than showing a
    // child nothing because they worked more days than the year has.
    ordered[ordered.length - 1]
  );
}

/** A term's activities, in the order they are meant to be met. */
export function termActivities(activities: Activity[], levelSlug: string, termIndex: number): Activity[] {
  return activities
    .filter(
      (activity) =>
        activity.is_active &&
        activity.level_slug === levelSlug &&
        activity.term_index === termIndex,
    )
    .sort(
      (a, b) =>
        (a.sequence_order ?? Number.MAX_SAFE_INTEGER) - (b.sequence_order ?? Number.MAX_SAFE_INTEGER) ||
        a.sort_order - b.sort_order ||
        a.title.localeCompare(b.title),
    );
}

/**
 * The activities for one day of a term.
 *
 * `dayInTerm` is 1-based. The window ends at the newest activity the child has
 * reached and reaches back over the ones most recently introduced; the day's
 * six are the new ones plus a spread of revisits, shuffled by a seed so the
 * order feels like a day rather than a list.
 */
export function activitiesForDay(
  termPool: Activity[],
  dayInTerm: number,
  seed: string,
): Activity[] {
  if (termPool.length === 0) return [];

  // Everything introduced by the end of today.
  const introduced = Math.min(termPool.length, Math.max(ACTIVITIES_PER_DAY, dayInTerm * NEW_PER_DAY));
  const frontier = termPool.slice(0, introduced);

  /**
   * Once the term's material is all introduced, STOP treating the last two as
   * "new".
   *
   * A term of 27 activities is fully introduced by day 14, and a term runs for
   * about 80 days. Without this branch the same final pair was pinned to every
   * one of the remaining 66 days — the child would have met a26 and a27 every
   * single morning for three months, which is worse than the shuffle this
   * replaced. Consolidation rotates a window across the whole term instead, so
   * everything comes round evenly and nothing dominates.
   */
  const exhausted = introduced >= termPool.length;
  if (exhausted && dayInTerm * NEW_PER_DAY > termPool.length) {
    // Stride ONE, not six. Advancing by the day's length looks natural and is
    // wrong: six and a thirty-activity term share a factor, so the window only
    // ever lands on five of the thirty start positions and the same block comes
    // up again and again. A stride of one visits every start position, so each
    // activity appears in exactly six days out of every `termPool.length`.
    const start = dayInTerm % termPool.length;
    const rotated: Activity[] = [];
    for (let step = 0; step < Math.min(ACTIVITIES_PER_DAY, termPool.length); step += 1) {
      rotated.push(termPool[(start + step) % termPool.length]);
    }
    return rotated.sort((a, b) => hash(`${seed}:order:${a.id}`) - hash(`${seed}:order:${b.id}`));
  }

  // Today's genuinely new ones sit at the end of the frontier.
  const newStart = Math.max(0, introduced - NEW_PER_DAY);
  const fresh = frontier.slice(newStart);

  // Revisits come from the window behind them, oldest allowed first.
  const revisitFrom = Math.max(0, newStart - REVISIT_WINDOW);
  const revisitPool = frontier.slice(revisitFrom, newStart);

  const chosen: Activity[] = [...fresh];
  const wanted = ACTIVITIES_PER_DAY - chosen.length;
  if (wanted > 0 && revisitPool.length > 0) {
    // Ordered by the seed so different days revisit different things, but
    // deterministically: the same day always revisits the same ones.
    const ranked = [...revisitPool].sort((a, b) => hash(seed + a.id) - hash(seed + b.id));
    chosen.push(...ranked.slice(0, wanted));
  }

  // A term with fewer activities than a day needs: top up from the frontier
  // rather than showing a short day.
  if (chosen.length < ACTIVITIES_PER_DAY) {
    for (const activity of frontier) {
      if (chosen.length >= ACTIVITIES_PER_DAY) break;
      if (!chosen.some((item) => item.id === activity.id)) chosen.push(activity);
    }
  }

  return chosen
    .slice(0, ACTIVITIES_PER_DAY)
    .sort((a, b) => hash(`${seed}:order:${a.id}`) - hash(`${seed}:order:${b.id}`));
}

/**
 * Where a child has reached, for the line a parent actually reads.
 *
 * Counted in DAYS WORKED, not days elapsed. A fortnight away from the app is a
 * fortnight not learned, and charging it against the year would tell a family
 * they had fallen behind for taking Eid off.
 */
export function levelProgress(daysCompleted: number, level: Level | null, terms: Term[]) {
  const total = level?.days_total ?? 250;
  const dayIndex = Math.min(total, daysCompleted + 1);
  const term = termForDay(terms, dayIndex);
  return {
    dayIndex,
    total,
    term,
    termIndex: term?.term_index ?? 1,
    termCount: terms.length || 3,
    dayInTerm: term ? dayIndex - term.start_day + 1 : dayIndex,
    termDays: term ? term.end_day - term.start_day + 1 : total,
    percent: Math.round((dayIndex / total) * 100),
  };
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Enrichment: the optional one or two beside the day, not inside it.
 * ───────────────────────────────────────────────────────────────────────────*/

/** How many optional extras a day may offer. Never required, never counted. */
export const ENRICHMENT_PER_DAY = 2;

/** The three bands enrichment is authored for, coarsest split that still fits. */
export type EnrichmentBand = "early" | "middle" | "upper";

const BAND_BY_LEVEL: Record<string, EnrichmentBand> = {
  playgroup: "early",
  nursery: "early",
  kg: "early",
  "class-1": "middle",
  "class-2": "middle",
  "class-3": "upper",
  "class-4": "upper",
  "class-5": "upper",
};

/**
 * Which band of enrichment a child should be offered.
 *
 * Falls back on the legacy stage so a learner created before levels existed
 * still gets extras rather than none.
 */
export function bandForLevel(levelSlug: string | null, stage?: string): EnrichmentBand {
  if (levelSlug && BAND_BY_LEVEL[levelSlug]) return BAND_BY_LEVEL[levelSlug];
  return stage === "primary" ? "middle" : "early";
}

/**
 * The day's optional extras.
 *
 * Deliberately NOT the core planner. Enrichment has no term to run through and
 * nothing to consolidate, so there is no window, no "new versus revisit" and no
 * catch-up: a child who skips a fortnight of extras has missed nothing they are
 * owed. What matters instead is BREADTH — that a child meets music one morning
 * and money the next rather than four movement tasks in a row — so the strands
 * are dealt round-robin and the day takes the next slice off that stream.
 *
 * ── The stride trap ─────────────────────────────────────────────────────────
 * activitiesForDay above records what happens when the stride and the pool
 * length share a factor: the window only ever lands on a fraction of the start
 * positions and most of the pool is never seen. Advancing by ENRICHMENT_PER_DAY
 * looks like the same mistake, and would be if we only ever read the FIRST item
 * of the window. We read both, and they are adjacent, so every index is still
 * reached: an even stride over an even length covers the even positions as
 * first-pick and the odd ones as second. The rotation simply repeats with a
 * period of ceil(pool / 2) days, which is the intended behaviour.
 */
export function enrichmentForDay(
  pool: Activity[],
  dayNumber: number,
  count = ENRICHMENT_PER_DAY,
): Activity[] {
  const live = pool.filter((activity) => activity.is_active);
  if (live.length === 0 || count <= 0) return [];

  // Round-robin by strand, so neighbours in the stream are different subjects.
  // Sorted by area first so the stream is stable whatever order the rows arrive
  // in — two devices building the same Tuesday must build the same Tuesday.
  const strands = [...new Set(live.map((activity) => activity.area))].sort();
  const byStrand = strands.map((strand) =>
    live
      .filter((activity) => activity.area === strand)
      .sort((a, b) => a.sort_order - b.sort_order || a.slug.localeCompare(b.slug)),
  );
  const longest = Math.max(...byStrand.map((list) => list.length));
  const rotation: Activity[] = [];
  for (let index = 0; index < longest; index += 1) {
    for (const list of byStrand) {
      if (list[index]) rotation.push(list[index]);
    }
  }

  const start = (dayNumber * count) % rotation.length;
  const chosen: Activity[] = [];
  for (let step = 0; step < Math.min(count, rotation.length); step += 1) {
    chosen.push(rotation[(start + step) % rotation.length]);
  }
  return chosen;
}
